import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  avaliacao as api,
  ESTADOS_DO_CLIENTE,
  ESTADOS_TECNICOS,
  type AvaliacaoNaLista,
  type EstadoDoCliente,
  type EstadoTecnico,
  type FiltroDeAvaliacoes,
  type NotaDaAvaliacao,
  type PaginaDeAvaliacoes,
} from '../api/avaliacao'
import { ErroApi } from '../api/client'
import { Botao } from '../componentes/Botao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import './avaliacoes.css'

/**
 * O que a equipe apontou, e o que foi feito com cada apontamento (demandas 209 e 235).
 *
 * ## 🔴 A pergunta que ela responde
 *
 * *"As conversas não estão boas"* — dito em 08/09 — não é acionável. **"Sete
 * apontamentos esta semana, cinco deles no mesmo tipo de turno"** é. A tela
 * existe para transformar a impressão espalhada em padrão visível.
 *
 * ## ⭐ E, desde a 235, o caminho de volta
 *
 * O 👎 sumia depois de clicado: ninguém sabia se foi lido, corrigido, nem em
 * qual versão. Agora cada apontamento tem **dois estados** — o da correção e o
 * da validação de quem apontou — e o placar do topo é o que se lê numa reunião.
 *
 * ⭐ **O trecho do turno vem ao lado do comentário, e é o que faz a tela ser
 * lida.** Uma listagem de *"conversa 122 · errado · Guilherme"* obriga a abrir
 * cada linha no Chatwoot; com a frase avaliada junto, o padrão aparece na
 * rolagem.
 */

/** 👍 fica fora da fila de triagem: o padrão da tela é o que pede correção. */
const FILTRO_INICIAL: FiltroDeAvaliacoes = { nota: 'Ruim' }

/** Cabe a fila inteira de 👎 do projeto numa página — não há paginação a navegar. */
const TAMANHO = 200

/** O que o painel de correção precisa saber: quais itens, e o estado de partida. */
type Triagem = { ids: number[]; inicial?: AvaliacaoNaLista }

export function Avaliacoes() {
  const { t } = useTranslation()

  const [pagina, setPagina] = useState<PaginaDeAvaliacoes | null>(null)
  const [filtro, setFiltro] = useState<FiltroDeAvaliacoes>(FILTRO_INICIAL)
  const [falhou, setFalhou] = useState(false)
  const [semPermissao, setSemPermissao] = useState(false)
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [triagem, setTriagem] = useState<Triagem | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    setSemPermissao(false)

    try {
      setPagina(await api.listar({ ...filtro, tamanho: TAMANHO }))
    } catch (e) {
      // 403 é diferente de erro: quem não tem `AvaliacaoVer` precisa ler "você
      // não tem acesso", e não "algo deu errado" — que faria pedir suporte.
      if (e instanceof ErroApi && e.status === 403) setSemPermissao(true)
      else setFalhou(true)
    }
  }, [filtro])

  useEffect(() => {
    void carregar()
  }, [carregar])

  // Seleção de itens que saíram da lista não pode ir num lote.
  useEffect(() => {
    if (!pagina) return
    const visiveis = new Set(pagina.itens.map((a) => a.id))
    setSelecionados((atual) => new Set([...atual].filter((id) => visiveis.has(id))))
  }, [pagina])

  const alterarFiltro = (parcial: Partial<FiltroDeAvaliacoes>) =>
    setFiltro((atual) => ({ ...atual, ...parcial }))

  const alternarSelecao = (id: number) =>
    setSelecionados((atual) => {
      const novo = new Set(atual)
      if (novo.has(id)) novo.delete(id)
      else novo.add(id)
      return novo
    })

  if (semPermissao) return <Estado tipo="semPermissao" />

  if (falhou)
    return (
      <Estado
        tipo="erro"
        descricao={t('avaliacao.pagina.falhou')}
        acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>}
      />
    )

  if (!pagina) return <Estado tipo="carregando" />

  const { itens, total, podeTriar, contagem } = pagina
  const filtrado = JSON.stringify(filtro) !== JSON.stringify(FILTRO_INICIAL)

  return (
    <main className="avaliacoes">
      <header className="avaliacoes__cabecalho">
        <div>
          <h1 className="avaliacoes__titulo">{t('avaliacao.pagina.titulo')}</h1>
          <p className="avaliacoes__descricao">{t('avaliacao.pagina.descricao')}</p>
        </div>

        <p className="avaliacoes__total">{t('avaliacao.pagina.total', { total })}</p>
      </header>

      <Placar contagem={contagem} filtro={filtro} definir={setFiltro} />

      <Filtros
        filtro={filtro}
        pagina={pagina}
        alterar={alterarFiltro}
        limpar={filtrado ? () => setFiltro(FILTRO_INICIAL) : undefined}
      />

      {podeTriar && selecionados.size > 0 && (
        <div className="avaliacoes__lote" role="region" aria-label={t('avaliacao.pagina.marcarComo')}>
          <span>{t('avaliacao.pagina.selecionados', { total: selecionados.size })}</span>
          <Botao variante="primario" onClick={() => setTriagem({ ids: [...selecionados] })}>
            {t('avaliacao.pagina.marcarComo')}
          </Botao>
          <Botao variante="discreto" onClick={() => setSelecionados(new Set())}>
            {t('avaliacao.pagina.limparSelecao')}
          </Botao>
        </div>
      )}

      {itens.length === 0 ? (
        <Estado
          tipo="vazio"
          descricao={t(filtrado ? 'avaliacao.pagina.vazioFiltrado' : 'avaliacao.pagina.vazio')}
          acao={
            filtrado ? (
              <Botao onClick={() => setFiltro(FILTRO_INICIAL)}>
                {t('avaliacao.pagina.limparFiltros')}
              </Botao>
            ) : (
              <p className="avaliacoes__dica">{t('avaliacao.pagina.vazioDica')}</p>
            )
          }
        />
      ) : (
        <ul className="avaliacoes__lista">
          {itens.map((a) => (
            <Item
              key={a.id}
              a={a}
              meuId={pagina.meuId}
              podeTriar={podeTriar}
              selecionado={selecionados.has(a.id)}
              alternar={() => alternarSelecao(a.id)}
              triar={() => setTriagem({ ids: [a.id], inicial: a })}
              recarregar={carregar}
            />
          ))}
        </ul>
      )}

      {triagem && (
        <PainelDeTriagem
          triagem={triagem}
          fechar={() => setTriagem(null)}
          gravado={() => {
            setTriagem(null)
            setSelecionados(new Set())
            void carregar()
          }}
        />
      )}
    </main>
  )
}

// ------------------------------------------------------------------ placar

function Placar({
  contagem,
  filtro,
  definir,
}: {
  contagem: PaginaDeAvaliacoes['contagem']
  filtro: FiltroDeAvaliacoes
  definir: (f: FiltroDeAvaliacoes) => void
}) {
  const { t } = useTranslation()

  return (
    <section className="avaliacoes__placar" aria-label={t('avaliacao.pagina.placar')}>
      <div className="avaliacoes__placar-grupo" role="group" aria-label={t('avaliacao.pagina.placarTecnico')}>
        <span className="avaliacoes__placar-titulo">{t('avaliacao.pagina.placarTecnico')}</span>
        {ESTADOS_TECNICOS.map((estado) => (
          <button
            key={estado}
            type="button"
            className={`avaliacoes__selo avaliacoes__selo--${estado}`}
            aria-pressed={filtro.estadoTecnico === estado && !filtro.estadoDoCliente}
            onClick={() => definir({ nota: 'Ruim', estadoTecnico: estado })}
          >
            {t(`avaliacao.tecnico.${estado}`)}{' '}
            <strong className="avaliacoes__numero">{contagem.tecnico[estado] ?? 0}</strong>
          </button>
        ))}
      </div>

      <div className="avaliacoes__placar-grupo" role="group" aria-label={t('avaliacao.pagina.placarCliente')}>
        <span className="avaliacoes__placar-titulo">{t('avaliacao.pagina.placarCliente')}</span>
        {ESTADOS_DO_CLIENTE.map((estado) => (
          <button
            key={estado}
            type="button"
            className={`avaliacoes__selo avaliacoes__selo--${estado}`}
            aria-pressed={filtro.estadoDoCliente === estado}
            onClick={() =>
              definir({
                nota: 'Ruim',
                // "Não validado" reabre o item — o técnico volta a Lido; os
                // outros dois só existem em cima de um Solucionado.
                estadoTecnico: estado === 'NaoValidado' ? undefined : 'Solucionado',
                estadoDoCliente: estado,
              })
            }
          >
            {t(`avaliacao.cliente.${estado}`)}{' '}
            <strong className="avaliacoes__numero">{contagem.cliente[estado] ?? 0}</strong>
          </button>
        ))}
      </div>
    </section>
  )
}

// ----------------------------------------------------------------- filtros

function Filtros({
  filtro,
  pagina,
  alterar,
  limpar,
}: {
  filtro: FiltroDeAvaliacoes
  pagina: PaginaDeAvaliacoes
  alterar: (parcial: Partial<FiltroDeAvaliacoes>) => void
  limpar?: () => void
}) {
  const { t } = useTranslation()

  // Texto livre só vale ao sair do campo ou com Enter: filtrar a cada tecla
  // dispararia uma consulta por letra digitada.
  const [conversa, setConversa] = useState(filtro.conversaExterna?.toString() ?? '')
  const [ficha, setFicha] = useState(filtro.ficha ?? '')

  useEffect(() => setConversa(filtro.conversaExterna?.toString() ?? ''), [filtro.conversaExterna])
  useEffect(() => setFicha(filtro.ficha ?? ''), [filtro.ficha])

  const aplicarTexto = () =>
    alterar({
      conversaExterna: /^\d+$/.test(conversa.trim()) ? Number(conversa.trim()) : undefined,
      ficha: ficha.trim() || undefined,
    })

  const aoEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') aplicarTexto()
  }

  return (
    <div className="avaliacoes__filtros-bloco">
      <div className="avaliacoes__filtros" role="group" aria-label={t('avaliacao.pagina.titulo')}>
        <Filtro atual={filtro.nota} valor={undefined} definir={(nota) => alterar({ nota })}>
          {t('avaliacao.pagina.todas')}
        </Filtro>
        <Filtro atual={filtro.nota} valor="Ruim" definir={(nota) => alterar({ nota })}>
          {t('avaliacao.pagina.somenteRuins')}
        </Filtro>
        <Filtro atual={filtro.nota} valor="Boa" definir={(nota) => alterar({ nota })}>
          {t('avaliacao.pagina.somenteBoas')}
        </Filtro>
      </div>

      {/* ⚠️ Rótulo por `htmlFor`, e não envolvendo o campo: um `<label>` que
          envolve o `<select>` põe a opção escolhida DENTRO do nome acessível
          ("Correção Qualquer"), e o leitor de tela lê as duas coisas juntas. */}
      <div className="avaliacoes__campos" role="group" aria-label={t('avaliacao.pagina.filtros')}>
        <CampoDeFiltro rotulo={t('avaliacao.pagina.estadoTecnico')}>
          {(id) => (
            <select
              id={id}
              className="campo__entrada"
              value={filtro.estadoTecnico ?? ''}
              onChange={(e) => alterar({ estadoTecnico: (e.target.value || undefined) as EstadoTecnico | undefined })}
            >
              <option value="">{t('avaliacao.pagina.qualquer')}</option>
              {ESTADOS_TECNICOS.map((estado) => (
                <option key={estado} value={estado}>
                  {t(`avaliacao.tecnico.${estado}`)}
                </option>
              ))}
            </select>
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('avaliacao.pagina.estadoDoCliente')}>
          {(id) => (
            <select
              id={id}
              className="campo__entrada"
              value={filtro.estadoDoCliente ?? ''}
              onChange={(e) =>
                alterar({ estadoDoCliente: (e.target.value || undefined) as EstadoDoCliente | undefined })
              }
            >
              <option value="">{t('avaliacao.pagina.qualquer')}</option>
              {ESTADOS_DO_CLIENTE.map((estado) => (
                <option key={estado} value={estado}>
                  {t(`avaliacao.cliente.${estado}`)}
                </option>
              ))}
            </select>
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('avaliacao.pagina.assistente')}>
          {(id) => (
            <select
              id={id}
              className="campo__entrada"
              value={filtro.assistenteId ?? ''}
              onChange={(e) => alterar({ assistenteId: e.target.value ? Number(e.target.value) : undefined })}
            >
              <option value="">{t('avaliacao.pagina.qualquer')}</option>
              {pagina.assistentes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('avaliacao.pagina.autor')}>
          {(id) => (
            <select
              id={id}
              className="campo__entrada"
              value={filtro.autorId ?? ''}
              onChange={(e) => alterar({ autorId: e.target.value ? Number(e.target.value) : undefined })}
            >
              <option value="">{t('avaliacao.pagina.qualquer')}</option>
              {pagina.autores.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('avaliacao.pagina.desde')}>
          {(id) => (
            <input
              id={id}
              type="date"
              className="campo__entrada"
              value={filtro.desde ?? ''}
              onChange={(e) => alterar({ desde: e.target.value || undefined })}
            />
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('avaliacao.pagina.ate')}>
          {(id) => (
            <input
              id={id}
              type="date"
              className="campo__entrada"
              value={filtro.ate ?? ''}
              onChange={(e) => alterar({ ate: e.target.value || undefined })}
            />
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('avaliacao.pagina.numeroDaConversa')}>
          {(id) => (
            <input
              id={id}
              inputMode="numeric"
              className="campo__entrada"
              value={conversa}
              onChange={(e) => setConversa(e.target.value)}
              onBlur={aplicarTexto}
              onKeyDown={aoEnter}
            />
          )}
        </CampoDeFiltro>

        <CampoDeFiltro rotulo={t('avaliacao.pagina.ficha')}>
          {(id) => (
            <input
              id={id}
              className="campo__entrada"
              value={ficha}
              maxLength={20}
              onChange={(e) => setFicha(e.target.value)}
              onBlur={aplicarTexto}
              onKeyDown={aoEnter}
            />
          )}
        </CampoDeFiltro>

        {limpar && (
          <Botao variante="discreto" onClick={limpar}>
            {t('avaliacao.pagina.limparFiltros')}
          </Botao>
        )}
      </div>
    </div>
  )
}

function CampoDeFiltro({ rotulo, children }: { rotulo: string; children: (id: string) => React.ReactNode }) {
  const id = useId()

  return (
    <div className="avaliacoes__campo">
      <label htmlFor={id}>{rotulo}</label>
      {children(id)}
    </div>
  )
}

function Filtro({
  atual,
  valor,
  definir,
  children,
}: {
  atual: NotaDaAvaliacao | undefined
  valor: NotaDaAvaliacao | undefined
  definir: (v: NotaDaAvaliacao | undefined) => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      className="avaliacoes__filtro"
      aria-pressed={atual === valor}
      onClick={() => definir(valor)}
    >
      {children}
    </button>
  )
}

// -------------------------------------------------------------------- item

function Item({
  a,
  meuId,
  podeTriar,
  selecionado,
  alternar,
  triar,
  recarregar,
}: {
  a: AvaliacaoNaLista
  meuId: number
  podeTriar: boolean
  selecionado: boolean
  alternar: () => void
  triar: () => void
  recarregar: () => void
}) {
  const { t } = useTranslation()

  // A validação só aparece depois de algo ter sido feito: antes disso não há o
  // que conferir. "Não validado" reabre o item e continua visível.
  const mostraCliente = a.estadoTecnico === 'Solucionado' || a.estadoDoCliente !== 'Pendente'
  const aguardaOutraPessoa =
    a.estadoTecnico === 'Solucionado' && a.estadoDoCliente === 'Pendente' && !a.podeValidar && a.tecnicoPorId === meuId

  return (
    <li className={`avaliacoes__item avaliacoes__item--${a.nota.toLowerCase()}`}>
      <div className="avaliacoes__linha">
        {podeTriar && (
          <input
            type="checkbox"
            className="avaliacoes__marcar"
            checked={selecionado}
            onChange={alternar}
            aria-label={t('avaliacao.pagina.selecionar', { id: a.id })}
          />
        )}

        {/* ⚠️ O texto acompanha o ícone: nota que só existe em cor não
            chega a quem não distingue as duas (UI-09). */}
        <span className={`avaliacoes__nota avaliacoes__nota--${a.nota.toLowerCase()}`}>
          <span aria-hidden="true">{a.nota === 'Boa' ? '👍' : '👎'}</span>{' '}
          {t(`avaliacao.${a.nota === 'Boa' ? 'boa' : 'ruim'}`)}
        </span>

        <span className="avaliacoes__meta">
          {a.marca && `${a.marca} · `}
          {a.assistente && `${a.assistente} · `}
          {a.mensagemId === 0
            ? t('avaliacao.pagina.conversaInteira')
            : t('avaliacao.pagina.conversa', { numero: a.conversaExterna })}
        </span>

        <span className="avaliacoes__meta">
          {a.autor} · {formatar.dataHora(a.em)}
        </span>

        {/* 209 §8 (25/09): o 👎 numa nota interna não é sobre algo que o cliente leu. */}
        {a.origem === 'Sistema' && (
          <span className="avaliacoes__interna">{t('avaliacao.notaInterna')}</span>
        )}
      </div>

      {/* O que o assistente disse. Sem isto, cada linha exige uma aba
          nova para significar alguma coisa. */}
      {a.trecho && <p className="avaliacoes__trecho">{a.trecho}</p>}

      {a.comentario && <p className="avaliacoes__comentario">{a.comentario}</p>}

      {a.nota === 'Ruim' && (
        <div className="avaliacoes__ciclo">
          <div className="avaliacoes__linha">
            <span className={`avaliacoes__estado avaliacoes__estado--${a.estadoTecnico}`}>
              {t('avaliacao.pagina.estadoTecnico')}: {t(`avaliacao.tecnico.${a.estadoTecnico}`)}
            </span>

            {mostraCliente && (
              <span className={`avaliacoes__estado avaliacoes__estado--${a.estadoDoCliente}`}>
                {t('avaliacao.pagina.estadoDoCliente')}: {t(`avaliacao.cliente.${a.estadoDoCliente}`)}
              </span>
            )}

            {a.ficha && <span className="avaliacoes__meta">{t('avaliacao.pagina.fichaCurta', { ficha: a.ficha })}</span>}
            {a.shaNoAr && (
              <span className="avaliacoes__meta">
                {t('avaliacao.pagina.noAr', { sha: a.shaNoAr.slice(0, 7) })}
              </span>
            )}

            {podeTriar && (
              <Botao variante="discreto" onClick={triar}>
                {t('avaliacao.pagina.triar')}
              </Botao>
            )}
          </div>

          {a.relatoTecnico && (
            <p className="avaliacoes__relato">
              {a.relatoTecnico}
              {a.tecnicoPor && a.tecnicoEm && (
                <span className="avaliacoes__meta">
                  {' — '}
                  {t('avaliacao.pagina.porQuemQuando', { quem: a.tecnicoPor, quando: formatar.dataHora(a.tecnicoEm) })}
                </span>
              )}
            </p>
          )}

          {a.comoValidar && (
            <div className="avaliacoes__validar">
              <span className="avaliacoes__rotulo">{t('avaliacao.pagina.comoValidar')}</span>
              <p className="avaliacoes__relato">{a.comoValidar}</p>
            </div>
          )}

          {a.estadoDoCliente !== 'Pendente' && a.clientePor && a.clienteEm && (
            <div className="avaliacoes__validar">
              <span className="avaliacoes__rotulo">
                {t('avaliacao.pagina.validacaoDe', { quem: a.clientePor, quando: formatar.dataHora(a.clienteEm) })}
              </span>
              {a.relatoDoCliente && <p className="avaliacoes__relato">{a.relatoDoCliente}</p>}
            </div>
          )}

          {a.podeValidar && a.estadoDoCliente === 'Pendente' && <Validacao id={a.id} gravado={recarregar} />}

          {aguardaOutraPessoa && <p className="avaliacoes__dica">{t('avaliacao.pagina.aguardaOutraPessoa')}</p>}
        </div>
      )}
    </li>
  )
}

// --------------------------------------------------------------- validação

function Validacao({ id, gravado }: { id: number; gravado: () => void }) {
  const { t } = useTranslation()
  const idRelato = useId()

  const [negando, setNegando] = useState(false)
  const [relato, setRelato] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const enviar = async (estado: 'ValidadoOk' | 'NaoValidado') => {
    if (estado === 'NaoValidado' && !relato.trim()) {
      setErro(t('avaliacao.pagina.painel.obrigatorio'))
      return
    }

    setEnviando(true)
    setErro(null)

    try {
      await api.validar([id], estado, estado === 'NaoValidado' ? relato.trim() : null)
      gravado()
    } catch (e) {
      setErro(mensagemDoErro(e) ?? t('avaliacao.pagina.falhouAoGravar'))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="avaliacoes__validacao">
      {!negando ? (
        <div className="avaliacoes__linha">
          <Botao variante="primario" carregando={enviando} onClick={() => void enviar('ValidadoOk')}>
            {t('avaliacao.pagina.validadoOk')}
          </Botao>
          <Botao onClick={() => setNegando(true)}>{t('avaliacao.pagina.naoValidado')}</Botao>
        </div>
      ) : (
        <div className="campo">
          <label className="campo__rotulo" htmlFor={idRelato}>
            {t('avaliacao.pagina.oQueContinuaErrado')}
            <span className="campo__obrigatorio" aria-hidden="true">
              *
            </span>
          </label>
          <textarea
            id={idRelato}
            className="campo__entrada"
            rows={3}
            maxLength={2000}
            value={relato}
            aria-required
            onChange={(e) => setRelato(e.target.value)}
          />
          <div className="avaliacoes__linha">
            <Botao variante="primario" carregando={enviando} onClick={() => void enviar('NaoValidado')}>
              {t('avaliacao.pagina.confirmarNaoValidado')}
            </Botao>
            <Botao variante="discreto" onClick={() => setNegando(false)}>
              {t('avaliacao.pagina.cancelar')}
            </Botao>
          </div>
        </div>
      )}

      {erro && (
        <p className="campo__mensagem-erro" role="alert">
          <span aria-hidden="true">! </span>
          {erro}
        </p>
      )}
    </div>
  )
}

// --------------------------------------------------------- painel de triagem

function PainelDeTriagem({
  triagem,
  fechar,
  gravado,
}: {
  triagem: Triagem
  fechar: () => void
  gravado: () => void
}) {
  const { t } = useTranslation()
  const idTitulo = useId()
  const idEstado = useId()
  const idRelato = useId()
  const idComo = useId()
  const idFicha = useId()
  const primeiro = useRef<HTMLSelectElement>(null)

  const inicial = triagem.inicial
  const [estado, setEstado] = useState<EstadoTecnico>(
    inicial && inicial.estadoTecnico !== 'NaoLido' ? inicial.estadoTecnico : 'Lido',
  )
  const [relato, setRelato] = useState(inicial?.relatoTecnico ?? '')
  const [comoValidar, setComoValidar] = useState(inicial?.comoValidar ?? '')
  const [ficha, setFicha] = useState(inicial?.ficha ?? '')
  const [tentou, setTentou] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => primeiro.current?.focus(), [])

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fechar()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [fechar])

  const solucionado = estado === 'Solucionado'
  const exigeRelato = solucionado || estado === 'NaoSeraTratado'

  const faltaRelato = exigeRelato && !relato.trim()
  const faltaComo = solucionado && !comoValidar.trim()
  const faltaFicha = solucionado && !ficha.trim()

  const gravar = async () => {
    setTentou(true)
    if (faltaRelato || faltaComo || faltaFicha) return

    setEnviando(true)
    setErro(null)

    try {
      await api.triar(
        triagem.ids,
        estado,
        relato.trim() || null,
        solucionado ? comoValidar.trim() : null,
        solucionado ? ficha.trim() : null,
      )
      gravado()
    } catch (e) {
      setErro(mensagemDoErro(e) ?? t('avaliacao.pagina.falhouAoGravar'))
    } finally {
      setEnviando(false)
    }
  }

  const rotuloRelato = solucionado
    ? t('avaliacao.pagina.painel.oQueFoiFeito')
    : estado === 'NaoSeraTratado'
      ? t('avaliacao.pagina.painel.motivo')
      : t('avaliacao.pagina.painel.relato')

  return (
    <div className="avaliacoes__fundo" onClick={fechar}>
      <div
        className="avaliacoes__painel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={idTitulo} className="avaliacoes__painel-titulo">
          {triagem.ids.length === 1
            ? t('avaliacao.pagina.painel.tituloUm')
            : t('avaliacao.pagina.painel.tituloLote', { total: triagem.ids.length })}
        </h2>

        <div className="campo">
          <label className="campo__rotulo" htmlFor={idEstado}>
            {t('avaliacao.pagina.painel.estado')}
          </label>
          <select
            id={idEstado}
            ref={primeiro}
            className="campo__entrada"
            value={estado}
            onChange={(e) => setEstado(e.target.value as EstadoTecnico)}
          >
            {ESTADOS_TECNICOS.map((e) => (
              <option key={e} value={e}>
                {t(`avaliacao.tecnico.${e}`)}
              </option>
            ))}
          </select>
          {solucionado && <p className="campo__ajuda">{t('avaliacao.pagina.painel.ajudaSolucionado')}</p>}
        </div>

        <CampoDeTexto
          id={idRelato}
          rotulo={rotuloRelato}
          obrigatorio={exigeRelato}
          valor={relato}
          definir={setRelato}
          erro={tentou && faltaRelato ? t('avaliacao.pagina.painel.obrigatorio') : undefined}
        />

        {solucionado && (
          <>
            <CampoDeTexto
              id={idComo}
              rotulo={t('avaliacao.pagina.comoValidar')}
              ajuda={t('avaliacao.pagina.painel.comoValidarAjuda')}
              obrigatorio
              valor={comoValidar}
              definir={setComoValidar}
              erro={tentou && faltaComo ? t('avaliacao.pagina.painel.obrigatorio') : undefined}
            />

            <div className={`campo ${tentou && faltaFicha ? 'campo--erro' : ''}`}>
              <label className="campo__rotulo" htmlFor={idFicha}>
                {t('avaliacao.pagina.ficha')}
                <span className="campo__obrigatorio" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id={idFicha}
                className="campo__entrada"
                maxLength={20}
                value={ficha}
                aria-required
                aria-invalid={tentou && faltaFicha ? true : undefined}
                onChange={(e) => setFicha(e.target.value)}
              />
              <p className="campo__ajuda">{t('avaliacao.pagina.painel.fichaAjuda')}</p>
              {tentou && faltaFicha && (
                <p className="campo__mensagem-erro" role="alert">
                  <span aria-hidden="true">! </span>
                  {t('avaliacao.pagina.painel.obrigatorio')}
                </p>
              )}
            </div>
          </>
        )}

        {erro && (
          <p className="campo__mensagem-erro" role="alert">
            <span aria-hidden="true">! </span>
            {erro}
          </p>
        )}

        <div className="avaliacoes__linha avaliacoes__painel-acoes">
          <Botao variante="primario" carregando={enviando} onClick={() => void gravar()}>
            {t('avaliacao.pagina.painel.gravar')}
          </Botao>
          <Botao variante="discreto" onClick={fechar}>
            {t('avaliacao.pagina.cancelar')}
          </Botao>
        </div>
      </div>
    </div>
  )
}

function CampoDeTexto({
  id,
  rotulo,
  ajuda,
  obrigatorio,
  valor,
  definir,
  erro,
}: {
  id: string
  rotulo: string
  ajuda?: string
  obrigatorio?: boolean
  valor: string
  definir: (v: string) => void
  erro?: string
}) {
  return (
    <div className={`campo ${erro ? 'campo--erro' : ''}`}>
      <label className="campo__rotulo" htmlFor={id}>
        {rotulo}
        {obrigatorio && (
          <span className="campo__obrigatorio" aria-hidden="true">
            *
          </span>
        )}
      </label>
      <textarea
        id={id}
        className="campo__entrada"
        rows={3}
        maxLength={2000}
        value={valor}
        aria-required={obrigatorio || undefined}
        aria-invalid={erro ? true : undefined}
        onChange={(e) => definir(e.target.value)}
      />
      {ajuda && <p className="campo__ajuda">{ajuda}</p>}
      {erro && (
        <p className="campo__mensagem-erro" role="alert">
          <span aria-hidden="true">! </span>
          {erro}
        </p>
      )}
    </div>
  )
}

/** O `erro` que o back devolve no 400/404/409 — é o que diz ao usuário o que corrigir. */
function mensagemDoErro(e: unknown): string | null {
  if (!(e instanceof ErroApi)) return null
  const corpo = e.corpo as { erro?: unknown } | null
  return corpo && typeof corpo.erro === 'string' ? corpo.erro : null
}
